import { fetchServer } from "@features/utils";
import { useCallback, useRef } from "react";
import { useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  OnboardingState,
  clearOnboardingState,
  packPrompts,
} from "./use-onboarding";

// Build the prompt for the extend action based on user selection
const buildPromptForAction = (state: OnboardingState): string | undefined => {
  switch (state.selectedAction) {
    case "professional":
      // Use the selected pack to create a professional photo
      return (
        packPrompts[state.selectedPack || "professional_natural"] ||
        packPrompts.professional_natural
      );
    case "edit":
      // For edit, we'll open the studio directly without prompt
      return undefined;
    case "train":
      // For training, we don't need a prompt - just import
      return undefined;
    default:
      return undefined;
  }
};

export type ProcessResult = {
  success: boolean;
  navigateTo?: string;
  error?: string;
};

export const useProcessOnboarding = () => {
  const queryClient = useQueryClient();
  const isProcessingRef = useRef(false);

  const processOnboardingAction = useCallback(
    async (state: OnboardingState): Promise<ProcessResult> => {
      // Prevent duplicate calls
      if (isProcessingRef.current) {
        console.log(
          "[useProcessOnboarding] Already processing, skipping duplicate call"
        );
        return { success: false, error: "Already processing" };
      }
      isProcessingRef.current = true;

      try {
        // For "train" action, skip import and go directly to lora page
        // The user will upload images through the lora interface
        if (state.selectedAction === "train") {
          clearOnboardingState();
          return { success: true, navigateTo: `/portraits/lora/model` };
        }

        // Step 1: Import the image (for professional/edit actions)
        if (!state.imageDataUrl) {
          throw new Error("No image to import");
        }

        console.log("[useProcessOnboarding] Starting import...");

        const importResult = await fetchServer<{ id: string }>(
          "/api/images/import",
          {
            image: state.imageDataUrl,
            site: "portraits",
            project_id: null,
          }
        );

        if (!importResult?.id) {
          throw new Error("Failed to import image");
        }

        let imageId = importResult.id;
        console.log("[useProcessOnboarding] Import successful, id:", imageId);

        // Invalidate images list to show the new image
        queryClient.invalidateQueries(["images", "list"]);

        // Step 2: Determine what to do based on action
        const prompt = buildPromptForAction(state);
        let navigateTo: string;

        if (prompt) {
          const editedPicture = await fetchServer<{ id: string }[]>(
            `/api/images/${imageId}/extends`,
            {
              prompt,
              count: 2,
              size: "portrait",
            }
          );
          if (!editedPicture?.[0]?.id) {
            throw new Error("Failed to edit image");
          }
          imageId = editedPicture?.[0].id;

          // Proceed to unlike the original image too
          await fetchServer(`/api/images/${importResult.id}`, {
            archived: true,
          });
        }

        if (state.selectedAction === "edit" || !prompt) {
          navigateTo = `/portraits/studio/${imageId}#edit-objects`;
        } else {
          // For professional, navigate to studio for now
          navigateTo = `/portraits/`;
        }

        // Clear state and return navigation path (let caller handle navigation after animation)
        clearOnboardingState();

        return { success: true, navigateTo };
      } catch (error) {
        toast.error("Error creating your image. Please try again.");
        console.error("[useProcessOnboarding] Error:", error);
        isProcessingRef.current = false;
        return {
          success: false,
          error:
            error instanceof Error ? error.message : "Something went wrong",
        };
      }
    },
    [queryClient]
  );

  // Separate function to actually navigate (called after animation completes)
  const navigateToResult = useCallback((navigateTo: string) => {
    isProcessingRef.current = false;
    document.location = navigateTo;
  }, []);

  return { processOnboardingAction, navigateToResult };
};
